import { theme } from 'antd';
import { RemoteUnlockIcon } from 'assets/images/svg';
import { constants } from 'config/constants';
import SuperFetch from 'library/helpers/superFetch';
import { useManagePermittedRoles } from 'library/hooks';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDevicesByDeviceID } from 'reduxStore/devices/selectors';
import { Notification } from 'zComponents/atoms';
import { Button, Divider, Flex, Modal, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ACTION_UNLOCK = 1;
const DeviceRemoteUnlockModal = ({ visible: visibleProp, setVisible: setVisibleProp, children, deviceID }) => {
	//✅ Hooks
	const {
		token: { paddingLG, fontSizeSM, paddingSM, marginXS, paddingXL, colorWarning }
	} = theme.useToken();

	const device = useSelector((state) => selectDevicesByDeviceID(state, deviceID));

	// ✅ Generate selected device names
	const filteredDeviceNames = useMemo(() => (device ? device.name || '-' : '-'), [device]);

	// ✅ Permissions
	const canRemoteUnlock = useManagePermittedRoles('aCRemoteUnlock', deviceID);
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	// ✅ Local state
	const [internalVisible, setInternalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const isUnlockSupported = constants.UNLOCK_CATEGORIES.includes(device?.defaultCategoryID);

	const triggerTitle = !canRemoteUnlock
		? permissionDeniedMessage
		: !isUnlockSupported
			? 'This device doesn’t support remote unlocking'
			: 'Unlock Device';

	const handleCancel = useCallback(() => setVisible(false), [setVisible]);

	// Get integrationID based on the device's defaultCategoryID

	const handleUnlock = async () => {
		try {
			setLoading(true);
			const res = await SuperFetch.post(
				`/devices/unlock`,
				{ deviceIDs: [deviceID], actionID: ACTION_UNLOCK },
				{}
			);

			if (res.statusCode === 200) {
				Notification('success', res.info);
				handleCancel();
			} else {
				Notification('error', res.data?.[0]?.errmsg || 'An error occurred');
			}
		} catch (error) {
			Notification('error', error.message || 'The lock cannot be unlocked');
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<Tooltip title={triggerTitle} placement='top'>
				<span onClick={(e) => canRemoteUnlock && isUnlockSupported && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<RemoteUnlockIcon color='currentColor' />}
							disabled={!canRemoteUnlock || !isUnlockSupported || loading}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='center'
						style={{ marginTop: paddingXL, padding: `0px ${marginXS}px` }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loading} block>
							Cancel
						</Button>
						<Button
							onClick={handleUnlock}
							loading={loading}
							disabled={loading}
							type='button'
							block
							style={{
								backgroundColor: colorWarning,
								borderColor: colorWarning,
								color: '#fff'
							}}
						>
							Unlock
						</Button>
					</Flex>
				]}
				width={350}
				styles={{ content: { padding: `${paddingXL}px` } }}
			>
				<Flex vertical align='center' justify='center' gap={marginXS} style={{ marginBottom: paddingLG }}>
					<RemoteUnlockIcon width={80} height={80} />
					<Flex vertical gap={marginXS} style={{ padding: `${marginXS}px 0` }}>
						<Title
							level={5}
							style={{ margin: 0, lineHeight: '24px', textAlign: 'center', padding: `0 ${paddingLG}px` }}
						>
							{'Are you sure you want to remote unlock this device?'}
						</Title>
						<Text style={{ fontSize: fontSizeSM, lineHeight: '20px', textAlign: 'center' }}>
							This action will unlock the device remotely. Please ensure that you have the necessary
							permissions to perform this action.
						</Text>
					</Flex>
				</Flex>
				<Flex vertical gap={marginXS}>
					<Divider orientation='center' plain style={{ margin: 0, padding: 0, marginTop: 0 }}>
						<Text style={{ fontSize: fontSizeSM, fontWeight: 200 }}>Selected Devices</Text>
					</Divider>
					<Text style={{ fontSize: fontSizeSM, fontWeight: 200, textAlign: 'center' }}>
						{filteredDeviceNames}
					</Text>
				</Flex>
			</Modal>
		</>
	);
};

export default DeviceRemoteUnlockModal;
